import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { AuthSelectors } from "src/redux/Auth/selectors";
import { TopBarSelectors } from "src/redux/TopBar/selectors";
import { AuthActions } from "src/redux/Auth/actions";
import { ModalActions } from "src/redux/Modal/actions";

import PrivacyPolicy from "./PrivacyPolicy";
import TermsOfUse from "./TermsOfUse";
import Login from "./Login";
import { ResetForm } from "./Login/ForgotPassword";
import Modal from "../Modal";

import Button from "../../common/Button";

import logo from "src/asset/logo-ksl-white.png";
import motif from "src/asset/motif-full.png";

import styles from "./styles.module.scss";
import Spinner from "../Spinner";

export default ({ children }) => {
    const dispatch = useDispatch();
    const isReady = useSelector(AuthSelectors.IsReady);
    const isLoggedIn = useSelector(AuthSelectors.IsLoggedIn);
    // Consent gate: signed in, but the gateway refuses every UI request until
    // the terms are accepted. Keep the landing page underneath so the consent
    // modal sits on the same ground the sign-in dialog does.
    const termsRequired = useSelector((state) => state.Auth.termsRequired);
    const appReady = useSelector(AuthSelectors.AppReady);
    const appInitFailed = useSelector(AuthSelectors.AppInitFailed);
    const user = useSelector(AuthSelectors.User);
    const role = useSelector(TopBarSelectors.Role);
    const location = useLocation();

    useEffect(() => {
        // should be called only once at the start of the app
        // equivalent to "componentDidMount" of class style components
        if (!isLoggedIn) dispatch(AuthActions.Init());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Set data-ready on body when app is fully loaded.
    // Tests wait for body[data-ready] to know the UI is interactive.
    useEffect(() => {
        if (appReady) {
            document.body.setAttribute("data-ready", "true");
        } else {
            document.body.removeAttribute("data-ready");
        }
    }, [appReady]);

    // Arriving from a password-reset email: ?resetToken=...&resetEmail=... opens
    // the reset prompt (the emailed link routes back to our SPA). Strip the
    // params afterward so a refresh doesn't reopen it.
    useEffect(() => {
        if (!isReady || isLoggedIn) return;
        const params = new URLSearchParams(window.location.search);
        const token = params.get("resetToken");
        const email = params.get("resetEmail");
        if (!token || !email) return;
        params.delete("resetToken");
        params.delete("resetEmail");
        const qs = params.toString();
        window.history.replaceState(
            {},
            "",
            window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash
        );
        dispatch(
            ModalActions.Open(ResetForm, {
                width: 440,
                ignoreClickOutside: true,
                customProps: { email, token },
            })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isReady, isLoggedIn]);

    // /privacy and /terms open their document. Both are for signed-in users,
    // so a signed-out visitor lands on the plain landing page instead.
    useEffect(() => {
        if (!isReady || !isLoggedIn) return;
        const currentPath = location.pathname;
        switch (currentPath) {
            case "/privacy":
                dispatch(
                    ModalActions.Open(PrivacyPolicy, {
                        wide: true,
                        title: "TRU Technologies Privacy Policy",
                        long: true,
                    })
                );
                break;
            case "/terms":
                dispatch(
                    ModalActions.Open(TermsOfUse, {
                        wide: true,
                        title: "TRU Technologies Terms of Use",
                        long: true,
                    })
                );
                break;
            default:
                break;
        }
    }, [location, dispatch, isReady, isLoggedIn]);

    useEffect(() => {
        if (Object.keys(user).length !== 0 && !isLoggedIn) {
            window.onbeforeunload = (event) => {
                event.preventDefault();
                dispatch(ModalActions.Close());
                dispatch(AuthActions.Logout());
                return true;
            };
        }

        return () => {
            window.onbeforeunload = undefined;
        };
    }, [dispatch, isLoggedIn, user]);

    // spinner or just leave a blank screen
    if (!isReady) return null;

    // Infrastructure-level startup failure (feature flag fetch, settings
    // fetch failure, server-clock sync failure, etc.). NOT a credentials
    // issue — those are surfaced via the Login form's own error display.
    // Show a generic "service unavailable, try again later" message; the
    // technical chain is in the browser console.
    if (appInitFailed) {
        return (
            <div data-ready={appReady || undefined} data-testid="init-error" style={{
                display: "flex", flexDirection: "column", alignItems: "center",
                justifyContent: "center", height: "100vh", padding: 32,
                fontFamily: "system-ui, -apple-system, sans-serif", textAlign: "center",
                backgroundColor: "#ffffff", color: "#1a1a1a",
            }}>
                <h1 style={{ marginBottom: 16, color: "#1a1a1a", fontWeight: 600 }}>
                    Something went wrong
                </h1>
                <p style={{ maxWidth: 480, lineHeight: 1.5, color: "#444", fontSize: 16 }}>
                    We couldn't load TruWeb. Please try again in a few minutes.
                    If the problem keeps happening, contact{" "}
                    <a href="mailto:ops@kslnetwork.com"
                       style={{ color: "#0b66c2", textDecoration: "underline" }}>
                        ops@kslnetwork.com
                    </a>.
                </p>
            </div>
        );
    }

    // if we are logged in return early - unless the consent gate is up, which
    // keeps the landing view and its modal so the gate is drawn over it
    if (isLoggedIn && !termsRequired) {
        // allow us to do more fine grained styling, needs to be a constant class name
        return <div className={`${role}Wrapper`} data-ready={appReady || undefined}>{children}</div>;
    }

    return (
        <div className={styles.lander}>
            <header>
                <div>
                    <img alt="KSL" src={logo} width="85" height="26" />
                    <a
                        href="mailto:ops@kslnetwork.com"
                        className={styles.link}
                    >
                        Contact
                    </a>
                    {/*
				features are not yet implemented
				<span>|</span>
				<span className={styles.link}>Q&A</span>
				*/}
                </div>
            </header>

            <section>
                <h1>Korea Securities Lending</h1>
                <img alt="motif" src={motif} />
                <div className={styles.english}>
                    Korea Securities Lending (KSL) develops software and
                    technology systems for financial market infrastructure.
                    <br />
                    KSL’s Innovative Financial Service designation for TruWeb
                    ended on July 31, 2026.
                    <br />
                    The service previously operated under that designation is no
                    longer available.
                </div>
                <Button
                    large
                    value="Get Started"
                    onClick={() =>
                        dispatch(
                            ModalActions.Open(Login, {
                                small: true,
                                ignoreClickOutside: true,
                            })
                        )
                    }
                />
            </section>

            <footer>
                <div>
                    <p>© 2026 Korea Securities Lending. All rights reserved.</p>
                </div>
            </footer>

            <Modal />
            <Spinner />
        </div>
    );
};
